import { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Citrus, Droplet, Waves, Milk, Leaf, Filter } from "lucide-react";
import { INGREDIENTS } from "@/data/content";

gsap.registerPlugin(ScrollTrigger);

const ICONS = [Citrus, Droplet, Waves, Milk, Leaf, Filter];
const TINTS = ["bg-jasmin", "bg-mandariini", "bg-water", "bg-jasmin", "bg-water", "bg-mandariini"];

export default function IngredientsSection() {
  const root = useRef(null);
  const track = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();
      mm.add("(min-width: 1024px)", () => {
        const el = track.current;
        const getAmount = () => Math.max(0, el.scrollWidth - el.parentElement.clientWidth);
        const tween = gsap.to(el, {
          x: () => -getAmount(),
          ease: "none",
          scrollTrigger: {
            trigger: root.current,
            start: "top 15%",
            end: () => `+=${getAmount()}`,
            pin: true,
            scrub: 0.8,
            invalidateOnRefresh: true,
          },
        });
        return () => tween.scrollTrigger && tween.scrollTrigger.kill();
      });
      gsap.from("[data-ing-head] > *", {
        y: 40,
        opacity: 0,
        duration: 0.8,
        stagger: 0.1,
        ease: "power3.out",
        scrollTrigger: { trigger: root.current, start: "top 75%" },
      });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={root}
      data-testid="ingredients-section"
      className="py-24 md:py-32 overflow-hidden bg-white"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8" data-ing-head>
        <p className="eyebrow text-water-deep/80">Vitamiinisuodattimen sisältö</p>
        <h2 className="h-section font-heading mt-5 max-w-3xl font-semibold text-foreground" lang="fi">
          Ihonhoitoa jokaisessa pisarassa.
        </h2>
        <p className="mt-5 max-w-2xl text-base md:text-lg leading-relaxed text-muted-foreground">
          Vitamiini C -suihkusuodatin yhdistää mekaanisen suodatuksen ja ihonhoidolliset ainesosat.
        </p>
      </div>

      <div className="mt-12 md:mt-16">
        <div
          ref={track}
          className="flex gap-5 md:gap-7 overflow-x-auto no-scrollbar snap-x snap-mandatory px-5 sm:px-8 lg:overflow-visible lg:px-[max(1.25rem,calc((100vw-80rem)/2+2rem))]"
        >
          {INGREDIENTS.map((ing, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <article
                key={ing.name}
                data-testid={`ingredient-${ing.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`group w-[78vw] sm:w-[22rem] shrink-0 snap-start rounded-[2rem] ${TINTS[i % TINTS.length]} p-8 md:p-10 transition-transform duration-300 hover:-translate-y-1.5`}
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/80 text-water-deep shadow-sm">
                  <Icon size={24} strokeWidth={1.8} />
                </span>
                <h3 className="font-heading mt-7 text-2xl font-semibold text-foreground">{ing.name}</h3>
                <p className="mt-1 text-xs font-bold uppercase tracking-widest text-foreground/50">
                  {ing.note}
                </p>
                <ul className="mt-5 space-y-2.5">
                  {ing.points.map((pt) => (
                    <li key={pt} className="flex gap-2.5 text-sm leading-relaxed text-foreground/75">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-water-deep/60" />
                      {pt}
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
          <div className="w-2 shrink-0 lg:hidden" />
        </div>
        <p className="mt-6 px-5 sm:px-8 text-xs font-semibold uppercase tracking-widest text-muted-foreground lg:hidden">
          Pyyhkäise nähdäksesi kaikki ainesosat
        </p>
      </div>
    </section>
  );
}
